import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Camera, X, Upload, Image as ImageIcon, Loader2 } from "lucide-react";

interface JobPhoto {
  id: string;
  file_path: string;
  file_name: string;
  file_size: number;
  created_at: string;
}

interface JobPhotoManagerProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isCustomer?: boolean; // Add prop to indicate if current user is customer
}

export const JobPhotoManager = ({ jobId, open, onOpenChange, isCustomer = false }: JobPhotoManagerProps) => {
  const [photos, setPhotos] = useState<JobPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [canViewPhotos, setCanViewPhotos] = useState(false);
  const [currentUserType, setCurrentUserType] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<JobPhoto | null>(null);
  const { toast } = useToast();
  const maxPhotos = 5;

  useEffect(() => {
    if (open) {
      fetchPhotos();
      fetchCurrentUserType();
    }
  }, [open, jobId]);

  const fetchCurrentUserType = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      console.log('Fetching user type for photos...');
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type, insurance_approved')
        .eq('user_id', user.user.id)
        .maybeSingle();

      console.log('User profile for photos:', profile);
      setCurrentUserType(profile?.user_type || null);
      
      // Tradesmen can view photos for available jobs; customers can always view
      const canView = profile?.user_type === 'customer' || 
                      isCustomer || 
                      (profile?.user_type === 'tradesman');
      
      console.log('Can view photos:', canView, 'User type:', profile?.user_type, 'Is customer prop:', isCustomer);
      setCanViewPhotos(canView);
    } catch (error) {
      console.error('Error fetching user type:', error);
    }
  };

  // Check if current user can manage photos (only customers)
  const canManagePhotos = (currentUserType === 'customer' || isCustomer) && canViewPhotos;

  const fetchPhotos = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching photos for job (DB):', jobId);
      const { data, error } = await supabase
        .from('job_photos')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching photos from DB:', error);
        throw error;
      }

      if (data && data.length > 0) {
        console.log('Photos fetched from DB:', data.length);
        setPhotos(data);
      } else {
        // Fallback: list from storage using multiple possible prefixes
        console.log('No DB photos found. Trying storage fallbacks for job:', jobId);

        // Attempt to resolve additional folder names (short id, job number, etc.)
        const shortId = jobId?.slice(0, 8);
        let jobNumber: string | undefined;
        try {
          const { data: jobRow } = await supabase
            .from('jobs')
            .select('job_number')
            .eq('id', jobId)
            .maybeSingle();
          if (jobRow?.job_number) jobNumber = String(jobRow.job_number);
        } catch (e) {
          console.warn('Could not fetch job number for storage fallback:', e);
        }

        const prefixes = Array.from(
          new Set([
            jobId,
            shortId,
            jobNumber,
            jobNumber ? `job-${jobNumber}` : undefined,
          ].filter(Boolean) as string[])
        );

        console.log('Trying storage prefixes:', prefixes);

        const listOpts = { sortBy: { column: 'created_at', order: 'desc' as const }, limit: 100 };
        const lists = await Promise.all(
          prefixes.map(async (prefix) => {
            try {
              const { data: items, error: listErr } = await supabase.storage
                .from('job-photos')
                .list(prefix, listOpts);
              if (listErr) {
                console.warn(`Storage list error for prefix ${prefix}:`, listErr?.message || listErr);
                return [] as any[];
              }
              return (items || []).map((item) => ({ prefix, item }));
            } catch (err) {
              console.warn(`Storage list exception for prefix ${prefix}:`, err);
              return [] as any[];
            }
          })
        );

        const flatItems = lists.flat();
        const mapped: JobPhoto[] = flatItems.map(({ prefix, item }: any) => ({
          id: `storage:${prefix}/${item.name}`,
          file_path: `${prefix}/${item.name}`,
          file_name: item.name,
          file_size: (item as any).metadata?.size || 0,
          created_at: ((item as any).created_at || new Date().toISOString()) as string,
        }));

        // Deduplicate by file_path
        const uniqueByPath = new Map(mapped.map((p) => [p.file_path, p]));
        const uniquePhotos = Array.from(uniqueByPath.values());
        console.log('Photos mapped from storage (unique):', uniquePhotos.length);
        setPhotos(uniquePhotos);
      }
    } catch (error: any) {
      console.error('Photo fetch error:', error);
      toast({
        title: 'Error loading photos',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (photos.length + files.length > maxPhotos) {
      toast({
        title: "Too many photos",
        description: `You can only upload up to ${maxPhotos} photos per job.`,
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      // Get current user's profile ID
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.user.id)
        .maybeSingle();

      if (!profile) throw new Error("Profile not found");

      const uploadPromises = Array.from(files).map(async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} is too large. Maximum size is 5MB.`);
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${jobId}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

        // Upload to storage
        const { data: storageData, error: storageError } = await supabase.storage
          .from('job-photos')
          .upload(fileName, file);

        if (storageError) throw storageError;

        // Save to database
        const { data: dbData, error: dbError } = await supabase
          .from('job_photos')
          .insert({
            job_id: jobId,
            file_path: storageData.path,
            file_name: file.name,
            file_size: file.size,
            uploaded_by: profile.id, // Use profile ID instead of user ID
          })
          .select()
          .single();

        if (dbError) throw dbError;

        return dbData;
      });

      const uploadedPhotos = await Promise.all(uploadPromises);
      setPhotos(prev => [...uploadedPhotos, ...prev]);
      
      toast({
        title: "Photos uploaded successfully",
        description: `${uploadedPhotos.length} photo(s) uploaded to your job.`,
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removePhoto = async (photoId: string, filePath: string) => {
    try {
      // Delete from storage
      await supabase.storage.from('job-photos').remove([filePath]);
      
      // Delete from database
      await supabase.from('job_photos').delete().eq('id', photoId);
      
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      
      toast({
        title: "Photo removed",
        description: "The photo has been deleted successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error removing photo",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getPhotoUrl = (filePath: string) => {
    const { data } = supabase.storage
      .from('job-photos')
      .getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
        <DialogHeader>
          <DialogTitle>
            {canManagePhotos ? 'Manage Job Photos' : 'View Job Photos'}
          </DialogTitle>
          <DialogDescription>
            {canManagePhotos 
              ? `Upload or remove photos for this job. Up to ${maxPhotos} photos allowed.`
              : 'View photos related to this job.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {(canViewPhotos || photos.length > 0) ? (
            <>
              {/* Photo Count */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  Photos ({photos.length}/{maxPhotos})
                </span>
                {canManagePhotos && photos.length < maxPhotos && (
                  <Button
                    onClick={() => document.getElementById('photo-upload')?.click()}
                    disabled={isUploading}
                    variant="outline"
                    size="sm"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Add Photos
                  </Button>
                )}
              </div>

              {/* Photo Grid */}
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading photos...</span>
                </div>
              ) : photos.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {photos.map((photo) => (
                    <Card key={photo.id} className="relative group">
                      <CardContent className="p-2">
                        <img
                          src={getPhotoUrl(photo.file_path)}
                          alt={photo.file_name}
                          className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setSelectedPhoto(photo)}
                        />
                        {canManagePhotos && (
                          <Button
                            onClick={() => removePhoto(photo.id, photo.file_path)}
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            size="sm"
                            variant="destructive"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Camera className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No photos uploaded yet</p>
                </div>
              )}

              {/* Upload Area */}
              {canManagePhotos && photos.length < maxPhotos && (
                <Card
                  className={`border-2 border-dashed transition-colors cursor-pointer ${
                    dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('photo-upload')?.click()}
                >
                  <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                    {isUploading ? (
                      <>
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                        <p className="text-sm text-muted-foreground">Uploading photos...</p>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="h-8 w-8 text-muted-foreground mb-4" />
                        <p className="text-sm font-medium mb-2">Drop photos here or click to upload</p>
                        <p className="text-xs text-muted-foreground">
                          Maximum {maxPhotos} photos, up to 5MB each. JPG, PNG, WEBP supported.
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {canManagePhotos && (
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                />
              )}
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Access Restricted</p>
                <p className="text-xs">
                  You don't have permission to view job photos.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Maximized Photo Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
        <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[90vh] p-0 overflow-hidden mx-2 sm:mx-auto">
          <div className="relative flex items-center justify-center bg-black/90 min-h-[60vh]">
            <Button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 z-10 rounded-full bg-black/50 text-white hover:bg-black/70"
              size="sm"
              variant="ghost"
            >
              <X className="h-4 w-4" />
            </Button>
            {selectedPhoto && (
              <img
                src={getPhotoUrl(selectedPhoto.file_path)}
                alt={selectedPhoto.file_name}
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>
          {selectedPhoto && (
            <div className="p-4 border-t">
              <p className="text-sm font-medium">{selectedPhoto.file_name}</p>
              <p className="text-xs text-muted-foreground">
                Uploaded on {new Date(selectedPhoto.created_at).toLocaleDateString()}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};